import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useParams, useLocation } from 'react-router-dom';
import { Menu, X, ArrowRight, CheckCircle2, Phone, Mail, MapPin, ChevronDown, Facebook, Clock, HelpCircle } from 'lucide-react';
import { services, processSteps } from './data';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState(false);
  const location = useLocation();

  const handleNavClick = (e, id) => {
    if (location.pathname === '/') {
      e.preventDefault();
      const element = document.getElementById(id);
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsOpen(false);
  };

  return (
    <nav className="fixed w-full z-50 bg-slate-900/80 backdrop-blur-md border-b border-white/5">
      <div className="max-w-6xl mx-auto px-6 md:px-12 flex items-center justify-between py-2">
        <Link 
          to="/" 
          onClick={() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            setIsOpen(false);
          }}
          className="flex items-center gap-4 md:gap-2 lg:gap-4 font-serif text-lg md:text-xs lg:text-xl tracking-wide uppercase text-white hover:opacity-80 transition-opacity"
        >
          <img src="/logo-multiservice.png" alt="Multiservice logo" className="h-12 md:h-10 lg:h-24 w-auto object-contain" />
          Multiservice
        </Link>
        
        {/* Desktop Nav */}
        <div className="hidden md:flex items-center space-x-6 lg:space-x-12">
          <div 
            className="relative group"
            onMouseEnter={() => setIsDropdownOpen(true)}
            onMouseLeave={() => setIsDropdownOpen(false)}
          >
            <button className="flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-300 hover:text-white transition-colors">
              Ydelser <ChevronDown className={`w-4 h-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            {isDropdownOpen && (
              <div className="absolute top-full left-0 pt-4 w-64">
                <div className="bg-slate-800 border border-white/10 rounded-xl overflow-hidden shadow-2xl">
                  {services.map((service, idx) => (
                    <Link
                      key={service.id}
                      to={`/ydelse/${service.id}`}
                      className={`block px-6 py-5 text-[11px] uppercase tracking-[0.15em] font-bold text-slate-300 hover:bg-slate-700 hover:text-white transition-colors border-b border-white/5 last:border-0 ${idx === 0 ? 'mt-4' : ''}`}
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      {service.title}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <Link 
            to="/proces"
            className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-300 hover:text-white transition-colors"
          >
            Proces
          </Link>
          <Link 
            to="/om-os"
            className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-300 hover:text-white transition-colors"
          >
            Om os
          </Link>
          <Link 
            to="/kontakt"
            className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-300 hover:text-white transition-colors"
          >
            Kontakt
          </Link>
          <Link to="/kontakt" className="bg-white text-slate-900 rounded-full px-8 py-3 text-[10px] uppercase tracking-widest transition-opacity hover:opacity-90">
            Få et tilbud
          </Link>
        </div>

        {/* Mobile Toggle */}
        <button className="md:hidden text-white" onClick={() => setIsOpen(!isOpen)}>
          {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden bg-slate-900 px-6 pb-12 pt-6 flex flex-col space-y-6">
          <div className="space-y-4">
            <button 
              onClick={() => setIsMobileDropdownOpen(!isMobileDropdownOpen)}
              className="w-full flex items-center justify-between text-sm uppercase tracking-widest font-semibold text-slate-500 hover:text-white transition-colors"
            >
              Ydelser
              <ChevronDown className={`w-4 h-4 transition-transform ${isMobileDropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            {isMobileDropdownOpen && (
              <div className="pl-4 flex flex-col space-y-4">
                {services.map((service, idx) => (
                  <Link
                    key={service.id}
                    to={`/ydelse/${service.id}`}
                    className={`text-xs uppercase tracking-widest font-medium text-slate-300 ${idx === 0 ? 'mt-2' : ''}`}
                    onClick={() => {
                      setIsOpen(false);
                      setIsMobileDropdownOpen(false);
                    }}
                  >
                    {service.title}
                  </Link>
                ))}
              </div>
            )}
          </div>
          <Link 
            to="/proces"
            className="text-sm uppercase tracking-widest font-semibold text-white"
            onClick={() => setIsOpen(false)}
          >
            Proces
          </Link>
          <Link 
            to="/om-os"
            className="text-sm uppercase tracking-widest font-semibold text-white"
            onClick={() => setIsOpen(false)}
          >
            Om os
          </Link>
          <Link 
            to="/kontakt"
            className="text-sm uppercase tracking-widest font-semibold text-white"
            onClick={() => setIsOpen(false)}
          >
            Kontakt
          </Link>
        </div>
      )}
    </nav>
  );
};

const Hero = () => (
  <section className="pt-40 pb-20 md:pt-56 md:pb-32 bg-slate-900">
    <div className="max-w-6xl mx-auto px-6 md:px-12 text-center flex flex-col items-center">
      <span className="uppercase tracking-[0.3em] text-[10px] font-bold text-slate-400 mb-8 block">Professionel Service &amp; Kvalitet</span>
      <h1 className="font-serif text-5xl md:text-7xl text-white leading-[1.1] mb-8 max-w-4xl">
        Din lokale partner til <br />
        <span className="italic font-serif">hverdagens opgaver</span>
      </h1>
      <p className="font-sans text-slate-400 font-light text-lg md:text-xl leading-relaxed max-w-2xl mb-12">
        Vi tager os af alt fra vinduespudsning til professionel rengøring, så du kan bruge din tid på det, der betyder mest.
      </p>
      <div className="flex flex-col sm:flex-row gap-4">
        <a 
          href="https://www.fenster.dk/andersmultiservice/" 
          target="_blank" 
          rel="noopener noreferrer"
          className="bg-white text-slate-900 rounded-full px-10 py-4 text-xs uppercase tracking-widest transition-opacity hover:opacity-90"
        >
          Book vinduespudsning
        </a>
        <Link to="/kontakt" className="bg-slate-800 text-white rounded-full px-10 py-4 text-xs uppercase tracking-widest transition-colors hover:bg-slate-700">
          Kontakt os
        </Link>
      </div>
      <div className="mt-20 w-full max-w-5xl aspect-[4/5] md:aspect-[16/9] relative">
        <img 
          src="/billede.jpg" 
          alt="Professionel rengøring i gang" 
          className="w-full h-full object-cover object-center rounded-[2.5rem] shadow-2xl"
        />
      </div>
    </div>
  </section>
);

const useScrollReveal = () => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = React.useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return [ref, isVisible];
};

const ServiceCard = ({ service, index }) => {
  const [ref, isVisible] = useScrollReveal();
  const isEven = index % 2 === 0;

  return (
    <div 
      ref={ref}
      className={`grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-24 items-center py-8 md:py-12`}
    >
      <div className={`order-2 ${isEven ? 'md:order-1' : 'md:order-2'} transition-all duration-1000 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <img 
          src={`/${service.image}`} 
          alt={service.title} 
          className="w-full aspect-square object-cover rounded-2xl"
        />
      </div>
      <div className={`flex flex-col order-1 ${isEven ? 'md:order-2' : 'md:order-1'} transition-all duration-1000 delay-300 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <span className="uppercase tracking-[0.2em] text-[10px] font-bold text-slate-400 mb-6">Service 0{index + 1}</span>
        <h2 className="font-serif text-4xl text-slate-900 mb-6">{service.title}</h2>
        <p className="font-sans text-slate-600 font-light leading-relaxed mb-8">
          {service.description}
        </p>
        <ul className="space-y-4 mb-10">
          {['Professionelt udstyr', 'Højeste kvalitet', 'Pålidelig service'].map((point) => (
            <li key={point} className="flex items-center gap-4 text-sm text-slate-500 font-light">
              <ArrowRight className="w-5 h-5 text-slate-400" />
              {point}
            </li>
          ))}
        </ul>
        <Link to={`/ydelse/${service.id}`} className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-slate-900 group">
          Læs mere om {service.title} <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>
    </div>
  );
};

const Services = () => (
  <section id="ydelser" className="bg-white">
    <div className="max-w-6xl mx-auto px-6 md:px-12">
      {services.map((service, idx) => (
        <ServiceCard key={service.id} service={service} index={idx} />
      ))}
    </div>
  </section>
);

const BlueSection = () => (
  <section className="bg-slate-900 py-32 text-white overflow-hidden relative">
    <div className="max-w-6xl mx-auto px-6 md:px-12 relative z-10">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-16 lg:gap-24 items-center">
        <div>
          <span className="uppercase tracking-[0.3em] text-[10px] font-bold text-slate-400 mb-8 block">Vores Tilgang</span>
          <h2 className="font-serif text-4xl md:text-6xl leading-[1.2] mb-12">
            Vi tror på, at <span className="italic">god service</span> starter med opmærksomhed på detaljen.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-12 lg:mb-0">
            <div>
              <p className="text-slate-400 font-light leading-relaxed">
                Multiservice er grundlagt på ønsket om at levere en personlig og professionel oplevelse. Uanset om det er vinduespudsning eller grundig rengøring, går vi aldrig på kompromis.
              </p>
            </div>
            <div className="space-y-6">
              <div className="flex gap-4">
                <CheckCircle2 className="w-8 h-8 text-slate-400 shrink-0" />
                <p className="text-sm text-slate-300 font-light">Certificeret og forsikret personale for din tryghed.</p>
              </div>
              <div className="flex gap-4">
                <CheckCircle2 className="w-8 h-8 text-slate-400 shrink-0" />
                <p className="text-sm text-slate-300 font-light">Miljøvenlige rengøringsprodukter som standard.</p>
              </div>
              <div className="flex gap-4">
                <CheckCircle2 className="w-8 h-8 text-slate-400 shrink-0" />
                <p className="text-sm text-slate-300 font-light">Fleksibel booking der passer til din kalender.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Phone Styled Facebook Iframe */}
        <div className="relative mx-auto lg:mx-0">
          <div className="relative w-[280px] h-[580px] bg-slate-800 rounded-[3rem] border-[8px] border-slate-700 shadow-2xl overflow-hidden">
            {/* Notch */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-slate-700 rounded-b-2xl z-20 flex items-center justify-center">
              <div className="w-8 h-1 bg-slate-600 rounded-full"></div>
            </div>
            
            {/* Screen Content */}
            <div className="h-full w-full bg-white pt-6">
              <iframe 
                src="https://www.facebook.com/plugins/page.php?href=https%3A%2F%2Fwww.facebook.com%2Fprofile.php%3Fid%3D100078832273134&tabs=timeline&width=280&height=554&small_header=true&adapt_container_width=true&hide_cover=false&show_facepile=true&appId" 
                width="280" 
                height="554" 
                style={{ border: 'none', overflow: 'hidden' }} 
                scrolling="no" 
                frameBorder="0" 
                allowFullScreen={true} 
                allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                className="w-full h-full"
              ></iframe>
            </div>

            {/* Bottom Bar */}
            <div className="absolute bottom-1 left-1/2 -translate-x-1/2 w-20 h-1 bg-slate-400 rounded-full z-20"></div>
          </div>
          
          {/* Decorative Glow */}
          <div className="absolute -inset-4 bg-slate-400/20 blur-3xl -z-10 rounded-full"></div>
        </div>
      </div>
    </div>
    <div className="absolute top-0 right-0 w-1/3 h-full bg-white/5 -skew-x-12 transform translate-x-20"></div>
  </section>
);

const ProcessStep = ({ step, index }) => {
  const [ref, isVisible] = useScrollReveal();
  return (
    <div 
      ref={ref}
      style={{ transitionDelay: `${index * 150}ms` }}
      className={`bg-white border border-slate-100 rounded-[2.5rem] px-12 py-6 relative flex flex-col items-center text-center shadow-sm transition-all duration-1000 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
    >
      <div className="absolute -top-6 bg-slate-900 text-white font-serif text-2xl w-12 h-12 flex items-center justify-center rounded-full shadow-lg">
        {step.number}
      </div>
      <h3 className="font-serif text-xl text-slate-900 mb-4">{step.title}</h3>
      <p className="text-slate-600 font-light text-sm leading-relaxed">
        {step.text}
      </p>
    </div>
  );
};

const Process = () => (
  <section id="proces" className="bg-[#F5F3ED] py-12 md:py-16">
    <div className="max-w-6xl mx-auto px-6 md:px-12">
      <div className="text-center mb-12 flex flex-col items-center">
        <span className="uppercase tracking-[0.3em] text-[10px] font-bold text-slate-500 mb-6">Sådan gør vi</span>
        <h2 className="font-serif text-4xl md:text-5xl text-slate-900">En enkel proces</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {processSteps.map((step, idx) => (
          <ProcessStep key={step.number} step={step} index={idx} />
        ))}
      </div>
    </div>
  </section>
);

const Contact = () => {
  const [status, setStatus] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (response.ok) setStatus('Tak! Vi kontakter dig snarest.');
    } catch {
      setStatus('Der skete en fejl. Prøv venligst igen.');
    }
  };

  return (
    <section id="kontakt" className="bg-white py-12 md:py-16">
      <div className="max-w-6xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-20">
        <div>
          <span className="uppercase tracking-[0.3em] text-[10px] font-bold text-slate-500 mb-8 block">Kontakt</span>
          <h2 className="font-serif text-4xl md:text-5xl text-slate-900 mb-8 leading-tight">
            Lad os hjælpe dig med <br /><span className="italic">din næste opgave</span>
          </h2>
          <p className="text-slate-600 font-light mb-12">
            Har du spørgsmål eller ønsker du et uforpligtende tilbud? Udfyld formularen, eller kontakt os direkte.
          </p>
          <div className="space-y-6">
            <div className="flex items-center gap-4 group">
              <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-900 group-hover:bg-slate-900 group-hover:text-white transition-colors">
                <Phone className="w-6 h-6" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Ring til os</p>
                <a href="tel:+4578701899" className="text-slate-900 font-medium hover:text-slate-600 transition-colors">78 70 18 99</a>
              </div>
            </div>
            <div className="flex items-center gap-4 group">
              <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-900 group-hover:bg-slate-900 group-hover:text-white transition-colors">
                <Mail className="w-6 h-6" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400">E-mail</p>
                <a href="mailto:anders.multiservice@outlook.dk" className="text-slate-900 font-medium hover:text-slate-600 transition-colors">anders.multiservice@outlook.dk</a>
              </div>
            </div>
            <div className="flex items-center gap-4 group">
              <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-900 group-hover:bg-slate-900 group-hover:text-white transition-colors">
                <Facebook className="w-6 h-6" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Facebook</p>
                <a href="https://www.facebook.com/profile.php?id=100078832273134&locale=da_DK" target="_blank" rel="noopener noreferrer" className="text-slate-900 font-medium hover:text-slate-600 transition-colors italic">Besøg os på Facebook</a>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <input name="website_url" className="sr-only absolute" tabIndex="-1" autoComplete="off" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <input 
              type="text" 
              name="name" 
              placeholder="Navn" 
              required 
              className="w-full bg-[#f1f1f1] border-none rounded-2xl px-6 py-4 text-sm focus:ring-2 focus:ring-slate-900 outline-none transition-all"
            />
            <input 
              type="email" 
              name="email" 
              placeholder="E-mail" 
              required 
              className="w-full bg-[#f1f1f1] border-none rounded-2xl px-6 py-4 text-sm focus:ring-2 focus:ring-slate-900 outline-none transition-all"
            />
          </div>
          <select 
            name="service" 
            className="w-full bg-[#f1f1f1] border-none rounded-2xl px-6 py-4 text-sm focus:ring-2 focus:ring-slate-900 outline-none transition-all appearance-none"
          >
            <option value="">Vælg ydelse</option>
            {services.map(s => <option key={s.id} value={s.id}>{s.title}</option>)}
          </select>
          <textarea 
            name="message" 
            rows="5" 
            placeholder="Hvordan kan vi hjælpe?" 
            required 
            className="w-full bg-[#f1f1f1] border-none rounded-2xl px-6 py-4 text-sm focus:ring-2 focus:ring-slate-900 outline-none transition-all resize-none"
          ></textarea>
          <button type="submit" className="w-full bg-slate-900 text-white rounded-full py-5 text-xs uppercase tracking-widest font-bold hover:opacity-90 transition-opacity">
            Send besked
          </button>
          {status && <p className="text-center text-sm font-medium text-slate-900 mt-4">{status}</p>}
        </form>
      </div>
    </section>
  );
};

const AboutPage = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  return (
    <div className="bg-white">
      <section className="pt-40 pb-20 bg-slate-900">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <Link to="/" className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-slate-400 hover:text-white mb-12 transition-colors">
            <ArrowRight className="w-3 h-3 rotate-180" /> Tilbage til forsiden
          </Link>
          <h1 className="font-serif text-5xl md:text-7xl text-white mb-8">Om os</h1>
          <p className="text-slate-400 text-lg md:text-xl font-light max-w-2xl leading-relaxed">
            Vi er mere end bare et servicefirma. Vi er din dedikerede partner i at skabe rammerne for en bedre hverdag.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-20 items-center">
          <div>
            <span className="uppercase tracking-[0.3em] text-[10px] font-bold text-slate-500 mb-8 block">Vores Historie</span>
            <h2 className="font-serif text-4xl text-slate-900 mb-8 leading-tight">Bygget på tillid og <br/><span className="italic">håndværksmæssig stolthed</span></h2>
            <div className="space-y-6 text-slate-600 font-light leading-relaxed">
              <p>
                Multiservice startede med en simpel vision: At levere serviceydelser af så høj kvalitet, at vores kunder aldrig behøver at lede efter en anden leverandør. Vi har gennem årene bygget en virksomhed, der hviler på fundamentet af pålidelighed og personlig kontakt.
              </p>
              <p>
                I dag dækker vi hele Aalborg og omegn. Vi hjælper både private hjem og store virksomheder med at holde deres faciliteter i topform. Vores team består af dedikerede fagfolk, der alle deler den samme passion for detaljen.
              </p>
            </div>
          </div>
          <div className="relative">
            <img 
              src="/2.jpeg" 
              alt="Vores team" 
              className="w-full aspect-[4/5] object-cover rounded-[2.5rem]"
            />
            <div className="absolute -bottom-10 -left-10 bg-slate-900 p-10 rounded-[2rem] text-white hidden lg:block">
              <p className="text-4xl font-serif mb-2">10+</p>
              <p className="text-[10px] uppercase tracking-widest text-slate-400">Års erfaring i branchen</p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {[
              { title: 'Kvalitet', text: 'Vi går aldrig på kompromis med resultatet. Hvis du ikke er tilfreds, er vi ikke færdige.' },
              { title: 'Pålidelighed', text: 'En aftale er en aftale. Vi kommer til tiden og leverer som lovet.' },
              { title: 'Miljø', text: 'Vi passer på kloden ved kun at bruge de mest skånsomme og effektive produkter.' }
            ].map((value) => (
              <div key={value.title} className="bg-white p-12 rounded-[2.5rem] border border-slate-100">
                <h3 className="font-serif text-2xl text-slate-900 mb-4">{value.title}</h3>
                <p className="text-slate-600 font-light text-sm leading-relaxed">{value.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <Contact />
    </div>
  );
};

const ProcessPage = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  return (
    <div className="bg-white">
      <section className="pt-40 pb-20 bg-slate-900">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <Link to="/" className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-slate-400 hover:text-white mb-12 transition-colors">
            <ArrowRight className="w-3 h-3 rotate-180" /> Tilbage til forsiden
          </Link>
          <h1 className="font-serif text-5xl md:text-7xl text-white mb-8">Vores Proces</h1>
          <p className="text-slate-400 text-lg md:text-xl font-light max-w-2xl leading-relaxed">
            Vi har gjort det nemt og gennemskueligt at få professionel hjælp til dine opgaver. Her er hvordan vi arbejder.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-20 items-center">
          <div>
            <span className="uppercase tracking-[0.3em] text-[10px] font-bold text-slate-500 mb-8 block">Gennemsigtighed</span>
            <h2 className="font-serif text-4xl text-slate-900 mb-8 leading-tight">Fra første kontakt til <br/><span className="italic">færdigt resultat</span></h2>
            <div className="space-y-8">
              {processSteps.map((step) => (
                <div key={step.number} className="flex gap-6">
                  <div className="shrink-0 bg-slate-900 text-white font-serif text-xl w-12 h-12 flex items-center justify-center rounded-full">
                    {step.number}
                  </div>
                  <div>
                    <h3 className="font-serif text-xl text-slate-900 mb-2">{step.title}</h3>
                    <p className="text-slate-600 font-light leading-relaxed">
                      {step.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <img 
              src="service-execution.jpg" 
              alt="Arbejdsproces" 
              className="w-full aspect-[4/5] object-cover rounded-[2.5rem]"
            />
          </div>
        </div>
      </section>

      <BlueSection />
      <Contact />
    </div>
  );
};

const ServicePage = () => {
  const { id } = useParams();
  const service = services.find(s => s.id === id);
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  if (!service) return null;

  return (
    <div className="bg-white">
      <section className="pt-40 pb-20 bg-slate-900">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <Link to="/" className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-slate-400 hover:text-white mb-12 transition-colors">
            <ArrowRight className="w-3 h-3 rotate-180" /> Tilbage til forsiden
          </Link>
          <h1 className="font-serif text-5xl md:text-7xl text-white mb-8">{service.title}</h1>
          <p className="text-slate-400 text-lg md:text-xl font-light max-w-2xl leading-relaxed">
            {service.description}
          </p>
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="max-w-6xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-16 items-start">
          <div className="sticky top-32">
            <img 
              src={`/${service.image}`} 
              alt={service.title} 
              className="w-full aspect-square object-cover rounded-[2.5rem]"
            />
          </div>
          <div>
            <div className="mb-12">
              <h2 className="font-serif text-3xl text-slate-900 mb-6">Om vores {service.title.toLowerCase()}</h2>
              <p className="text-slate-600 font-light leading-relaxed mb-8">
                Vi tilbyder professionel {service.title.toLowerCase()} til både private og erhverv. 
                Vores tilgang er altid baseret på kvalitet, pålidelighed og en tæt dialog med kunden. 
                Vi benytter de nyeste metoder og de bedste materialer for at sikre et fejlfrit resultat hver gang.
              </p>
              <div className="grid grid-cols-1 gap-4">
                {[
                  'Skræddersyede løsninger',
                  'Fokus på detaljen',
                  'Miljøvenlige materialer',
                  'Erfarne fagfolk'
                ].map((item) => (
                  <div key={item} className="flex items-center gap-4 p-6 bg-slate-50 rounded-2xl">
                    <CheckCircle2 className="w-6 h-6 text-slate-400 shrink-0" />
                    <span className="text-sm font-medium text-slate-900">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-slate-900 p-12 rounded-[2.5rem] text-white">
              <h3 className="font-serif text-2xl mb-6">Book {service.title.toLowerCase()} i dag</h3>
              <p className="text-slate-400 text-sm font-light mb-8">
                {service.id === 'vinduespudsning' 
                  ? 'Bestil din vinduespudsning direkte online via vores bookingsystem.' 
                  : 'Udfyld formularen nedenfor eller kontakt os direkte for et uforpligtende tilbud på din opgave.'}
              </p>
              {service.id === 'vinduespudsning' ? (
                <a 
                  href="https://www.fenster.dk/andersmultiservice/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-block bg-white text-slate-900 rounded-full px-10 py-4 text-xs uppercase tracking-widest transition-opacity hover:opacity-90"
                >
                  Book online nu
                </a>
              ) : (
                <Link to="/kontakt" className="inline-block bg-white text-slate-900 rounded-full px-10 py-4 text-xs uppercase tracking-widest transition-opacity hover:opacity-90">
                  Kontakt os nu
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>
      <Contact />
    </div>
  );
};

const ContactPage = () => {
  const [status, setStatus] = useState('');
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (response.ok) {
        setStatus('Tak! Vi kontakter dig snarest.');
        e.target.reset();
      } else {
        setStatus('Der skete en fejl. Prøv venligst igen.');
      }
    } catch {
      setStatus('Der skete en fejl. Prøv venligst igen.');
    }
  };

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="pt-40 pb-20 bg-slate-900">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <Link to="/" className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-slate-400 hover:text-white mb-12 transition-colors">
            <ArrowRight className="w-3 h-3 rotate-180" /> Tilbage til forsiden
          </Link>
          <h1 className="font-serif text-5xl md:text-7xl text-white mb-8">Kontakt os</h1>
          <p className="text-slate-400 text-lg md:text-xl font-light max-w-2xl leading-relaxed">
            Vi sidder klar til at hjælpe dig med dine spørgsmål eller give et uforpligtende tilbud på din opgave. Udfyld formularen, ring eller send en mail.
          </p>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-20">
          {/* Contact Info Column */}
          <div>
            <span className="uppercase tracking-[0.3em] text-[10px] font-bold text-slate-500 mb-8 block">Information</span>
            <h2 className="font-serif text-4xl text-slate-900 mb-8 leading-tight">
              Lad os hjælpe dig med <br /><span className="italic">din næste opgave</span>
            </h2>
            <p className="text-slate-600 font-light mb-12 leading-relaxed">
              Du er altid velkommen til at kontakte os. Vi bestræber os på at besvare alle henvendelser inden for 24 timer på hverdage.
            </p>

            <div className="space-y-6 mb-12">
              <div className="flex items-center gap-4 group">
                <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-900 group-hover:bg-slate-900 group-hover:text-white transition-colors">
                  <Phone className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Ring til os</p>
                  <a href="tel:+4578701899" className="text-slate-900 font-medium hover:text-slate-600 transition-colors">78 70 18 99</a>
                </div>
              </div>
              <div className="flex items-center gap-4 group">
                <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-900 group-hover:bg-slate-900 group-hover:text-white transition-colors">
                  <Mail className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400">E-mail</p>
                  <a href="mailto:anders.multiservice@outlook.dk" className="text-slate-900 font-medium hover:text-slate-600 transition-colors">anders.multiservice@outlook.dk</a>
                </div>
              </div>
              <div className="flex items-center gap-4 group">
                <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-900 group-hover:bg-slate-900 group-hover:text-white transition-colors">
                  <MapPin className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Adresse</p>
                  <p className="text-slate-900 font-medium cursor-default">Hellevangen 63, 9210 Aalborg</p>
                </div>
              </div>
              <div className="flex items-center gap-4 group">
                <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-900 group-hover:bg-slate-900 group-hover:text-white transition-colors">
                  <Facebook className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Facebook</p>
                  <a href="https://www.facebook.com/profile.php?id=100078832273134&locale=da_DK" target="_blank" rel="noopener noreferrer" className="text-slate-900 font-medium hover:text-slate-600 transition-colors italic">Besøg os på Facebook</a>
                </div>
              </div>
            </div>

            {/* Opening Hours Card */}
            <div className="bg-slate-50 p-8 rounded-[2rem] border border-slate-100">
              <h3 className="font-serif text-xl text-slate-900 mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-slate-500" /> Åbningstider
              </h3>
              <div className="space-y-2 text-sm text-slate-600 font-light">
                <div className="flex justify-between border-b border-slate-200/60 pb-2">
                  <span>Mandag - Fredag</span>
                  <span className="font-medium text-slate-900">07:00 - 17:00</span>
                </div>
                <div className="flex justify-between pt-1">
                  <span>Lørdag - Søndag</span>
                  <span className="font-medium text-slate-500">Lukket</span>
                </div>
              </div>
            </div>
          </div>

          {/* Form Column */}
          <div className="bg-slate-50 p-8 md:p-12 rounded-[2.5rem] border border-slate-100">
            <h3 className="font-serif text-2xl text-slate-900 mb-6">Send os en besked</h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <input name="website_url" className="sr-only absolute" tabIndex="-1" autoComplete="off" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="flex flex-col gap-2">
                  <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 pl-2">Navn</label>
                  <input 
                    type="text" 
                    name="name" 
                    placeholder="Dit navn" 
                    required 
                    className="w-full bg-white border border-slate-200 rounded-2xl px-6 py-4 text-sm focus:ring-2 focus:ring-slate-900 outline-none transition-all"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 pl-2">E-mail</label>
                  <input 
                    type="email" 
                    name="email" 
                    placeholder="Din e-mail" 
                    required 
                    className="w-full bg-white border border-slate-200 rounded-2xl px-6 py-4 text-sm focus:ring-2 focus:ring-slate-900 outline-none transition-all"
                  />
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 pl-2">Ydelse</label>
                <select 
                  name="service" 
                  className="w-full bg-white border border-slate-200 rounded-2xl px-6 py-4 text-sm focus:ring-2 focus:ring-slate-900 outline-none transition-all appearance-none"
                >
                  <option value="">Vælg ydelse (valgfri)</option>
                  {services.map(s => <option key={s.id} value={s.id}>{s.title}</option>)}
                </select>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 pl-2">Besked</label>
                <textarea 
                  name="message" 
                  rows="5" 
                  placeholder="Hvordan kan vi hjælpe dig?" 
                  required 
                  className="w-full bg-white border border-slate-200 rounded-2xl px-6 py-4 text-sm focus:ring-2 focus:ring-slate-900 outline-none transition-all resize-none"
                ></textarea>
              </div>
              <button type="submit" className="w-full bg-slate-900 text-white rounded-full py-5 text-xs uppercase tracking-widest font-bold hover:opacity-90 transition-opacity">
                Send besked
              </button>
              {status && <p className="text-center text-sm font-medium text-slate-900 mt-4">{status}</p>}
            </form>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-slate-50 py-16 md:py-24 border-t border-slate-100">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="uppercase tracking-[0.3em] text-[10px] font-bold text-slate-500 mb-4 block">FAQ</span>
            <h2 className="font-serif text-4xl text-slate-900">Ofte stillede <br/><span className="italic">spørgsmål</span></h2>
          </div>

          <div className="space-y-8">
            {[
              {
                q: 'Hvordan får jeg et tilbud?',
                a: 'Du kan enten ringe til os på 78 70 18 99, sende en e-mail til anders.multiservice@outlook.dk eller udfylde vores kontaktformular her på siden. Vi vender tilbage med et uforpligtende tilbud hurtigst muligt.'
              },
              {
                q: 'Dækker I hele Aalborg?',
                a: 'Ja, vi dækker hele Aalborg og omegn. Hvis du bor lidt længere væk, er du stadig velkommen til at kontakte os – vi finder ofte en løsning.'
              },
              {
                q: 'Kan jeg booke vinduespudsning online?',
                a: 'Ja! Vi har et nemt online bookingsystem til vinduespudsning. Du kan klikke på "Book online" i menuen eller finde linket under vores ydelse for vinduespudsning.'
              },
              {
                q: 'Hvilke rengøringsmidler bruger I?',
                a: 'Vi går meget op i miljøet og dit indeklima. Derfor bruger vi udelukkende skånsomme, miljøvenlige og effektive produkter, der sikrer et rent resultat uden unødig kemi.'
              }
            ].map((faq, idx) => (
              <div key={idx} className="bg-white p-8 rounded-[2rem] border border-slate-100">
                <h3 className="font-serif text-lg text-slate-900 mb-3 flex items-start gap-3">
                  <HelpCircle className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
                  {faq.q}
                </h3>
                <p className="text-slate-600 font-light text-sm leading-relaxed pl-8">
                  {faq.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

const Footer = () => (
  <footer className="bg-slate-900 pt-24 pb-12 text-white border-t border-white/5">
    <div className="max-w-6xl mx-auto px-6 md:px-12">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-20">
        <div className="md:col-span-1">
          <Link 
            to="/" 
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="flex items-center gap-4 md:gap-2 lg:gap-4 font-serif text-base md:text-xs lg:text-sm uppercase tracking-wide mb-6 hover:opacity-80 transition-opacity"
          >
            <img src="/logo-multiservice.png" alt="Multiservice logo" className="h-10 md:h-8 lg:h-10 w-auto object-contain" />
            Multiservice
          </Link>
          <p className="text-slate-400 text-sm font-light leading-relaxed mb-6">
            Professionel hjælp til din hverdag. Vi dækker hele Aalborg og omegn med fokus på kvalitet og tillid.
          </p>
          <div className="space-y-2 text-sm text-slate-400 font-light">
            <a href="tel:+4578701899" className="flex items-center gap-2 hover:text-white transition-colors">
              <Phone className="w-4 h-4" /> 78 70 18 99
            </a>
            <a href="mailto:anders.multiservice@outlook.dk" className="flex items-center gap-2 hover:text-white transition-colors">
              <Mail className="w-4 h-4" /> anders.multiservice@outlook.dk
            </a>
            <p className="flex items-center gap-2 hover:text-white transition-colors cursor-default text-xs leading-tight">
              <MapPin className="w-4 h-4 shrink-0" /> Hellevangen 63, 9210 Aalborg
            </p>
          </div>
        </div>
        
        <div>
          <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-white mb-6">Ydelser</h3>
          <ul className="space-y-4">
            {services.map(service => (
              <li key={service.id} className="flex flex-col gap-1">
                <Link to={`/ydelse/${service.id}`} className="text-sm text-slate-400 hover:text-white transition-colors font-light">
                  {service.title}
                </Link>
                {service.id === 'vinduespudsning' && (
                  <a 
                    href="https://www.fenster.dk/andersmultiservice/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-[9px] uppercase tracking-widest text-slate-500 hover:text-white transition-colors font-bold"
                  >
                    → Book online her
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-white mb-6">Virksomheden</h3>
          <ul className="space-y-4">
            <li><Link to="/om-os" className="text-sm text-slate-400 hover:text-white transition-colors font-light">Om os</Link></li>
            <li><Link to="/proces" className="text-sm text-slate-400 hover:text-white transition-colors font-light">Vores proces</Link></li>
            <li><Link to="/kontakt" className="text-sm text-slate-400 hover:text-white transition-colors font-light">Kontakt</Link></li>
          </ul>
        </div>

        <div>
          <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-white mb-6">Følg os</h3>
          <div className="flex flex-col space-y-4">
            <a 
              href="https://www.facebook.com/profile.php?id=100078832273134&locale=da_DK" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors font-light"
            >
              <Facebook className="w-4 h-4 text-[#1877F2]" /> Facebook
            </a>
          </div>
        </div>
      </div>

      <div className="pt-12 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-6">
        <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
          © {new Date().getFullYear()} Multiservice. Alle rettigheder forbeholdes.
        </p>
        <div className="flex gap-8">
          <a href="#" className="text-[10px] uppercase tracking-widest font-bold text-slate-500 hover:text-white transition-colors">Privatlivspolitik</a>
          <a href="#" className="text-[10px] uppercase tracking-widest font-bold text-slate-500 hover:text-white transition-colors">Handelsbetingelser</a>
        </div>
      </div>
    </div>
  </footer>
);

function LandingPage() {
  return (
    <>
      <Hero />
      <Services />
      <BlueSection />
      <Process />
      <Contact />
    </>
  );
}

function App() {
  return (
    <div className="min-h-screen font-sans selection:bg-slate-900 selection:text-white">
      <Navbar />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/om-os" element={<AboutPage />} />
        <Route path="/proces" element={<ProcessPage />} />
        <Route path="/kontakt" element={<ContactPage />} />
        <Route path="/ydelse/:id" element={<ServicePage />} />
      </Routes>
      <Footer />
    </div>
  );
}

export default App;
